import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { SelectedStockProvider } from './contexts/SelectedStockContext';
import { PortfolioKPIProvider } from './contexts/PortfolioKPIContext';
import { BeginnerProgressProvider } from './contexts/BeginnerProgressContext';
import { FrameworkProvider } from './contexts/FrameworkContext';
import { StrategyProvider } from './contexts/StrategyContext';
import { PortfolioLensProvider } from './contexts/PortfolioLensContext';
import ErrorBoundary from './components/common/ErrorBoundary';
import ProtectedRoute from './components/common/ProtectedRoute';
import Homepage from './components/homepage/Homepage';
import RealYieldLanding from './components/landing/RealYieldLanding';
import PublicCoach from './components/coach/PublicCoach';
import PricingPage from './components/pricing/PricingPage';
import UpgradeModal from './components/common/UpgradeModal';
import LoginForm from './components/auth/LoginForm';
import RegisterForm from './components/auth/RegisterForm';
import VerifyEmailSent from './components/auth/VerifyEmailSent';
import VerifyEmail from './components/auth/VerifyEmail';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import {
  TermsPlaceholder,
  PrivacyPlaceholder,
} from './components/auth/AuthPlaceholder';
import Dashboard from './components/dashboard/Dashboard';
import StockScreener from './components/screener/StockScreener';
import StockAnalysis from './components/analysis/StockAnalysis';
import PortfolioPortfolio from './components/portfolio/PortfolioPortfolio';
import PortfolioPerformance from './components/portfolio/PortfolioPerformance';
import PortfolioPositions from './components/portfolio/PortfolioPositions';
import AICoach from './components/aicoach/AICoach';
import BacktestPage from './components/backtest/BacktestPage';
import AdminLogs from './components/admin/AdminLogs';
import CoachAnalytics from './components/admin/CoachAnalytics';
import DistributionPipeline from './components/admin/DistributionPipeline';
import SettingsPage from './components/settings/SettingsPage';
import ComboAnalytics from './components/dev/ComboAnalytics';
import LeaderboardPage from './components/leaderboard/LeaderboardPage';
import config from './config';
import { initMarketStatus } from './utils/marketHours';

function App() {
  // Initialize market status cache on app load (for holiday detection)
  useEffect(() => {
    initMarketStatus();
  }, []);
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BeginnerProgressProvider>
          <SelectedStockProvider>
            <FrameworkProvider>
            <Router>
              <StrategyProvider>
              <PortfolioLensProvider>
              <PortfolioKPIProvider>
            <div className="App">
            <UpgradeModal />
            <Routes>
            {/* Public routes */}
            <Route path="/" element={<Homepage />} />
            <Route path="/real-yield" element={<RealYieldLanding />} />
            <Route path="/coach" element={<PublicCoach />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/login" element={<LoginForm />} />
            <Route path="/register" element={<RegisterForm />} />
            <Route path="/verify-email-sent" element={<VerifyEmailSent />} />
            <Route path="/verify-email/:token" element={<VerifyEmail />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:token" element={<ResetPassword />} />
            <Route path="/terms" element={<TermsPlaceholder />} />
            <Route path="/privacy" element={<PrivacyPlaceholder />} />
            
            {/* Protected routes */}
            <Route
              path="/dive-in"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            {/* Legacy route redirect */}
            <Route
              path="/dashboard"
              element={<Navigate to="/dive-in" replace />}
            />
            <Route
              path="/screening"
              element={
                <ProtectedRoute>
                  <StockScreener />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analysis"
              element={
                <ProtectedRoute>
                  <StockAnalysis />
                </ProtectedRoute>
              }
            />
            {/* Portfolio routes - three-tab architecture */}
            <Route
              path="/portfolio"
              element={<Navigate to="/portfolio/portfolio" replace />}
            />
            <Route
              path="/portfolio/positions"
              element={
                <ProtectedRoute>
                  <PortfolioPositions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/portfolio/portfolio"
              element={
                <ProtectedRoute>
                  <PortfolioPortfolio />
                </ProtectedRoute>
              }
            />
            <Route
              path="/portfolio/performance"
              element={
                <ProtectedRoute>
                  <PortfolioPerformance />
                </ProtectedRoute>
              }
            />
            <Route
              path="/portfolio/trades"
              element={<Navigate to="/portfolio/performance" replace />}
            />
            {/* Replay hidden (Oct 2026) — old links land on Positions. */}
            <Route
              path="/portfolio/replay"
              element={<Navigate to="/portfolio/positions" replace />}
            />
            {/* Legacy redirect */}
            <Route
              path="/portfolio/discipline"
              element={<Navigate to="/portfolio/positions" replace />}
            />
            {/* Legacy redirect */}
            <Route
              path="/portfolio/performance"
              element={<Navigate to="/portfolio/portfolio" replace />}
            />
            <Route
              path="/ai-coach"
              element={
                <ProtectedRoute>
                  <AICoach />
                </ProtectedRoute>
              }
            />
            <Route
              path="/backtest"
              element={
                <ProtectedRoute>
                  <BacktestPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leaderboard"
              element={
                <ProtectedRoute>
                  <LeaderboardPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/logs"
              element={
                <ProtectedRoute>
                  <AdminLogs />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/coach"
              element={
                <ProtectedRoute>
                  <CoachAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin/distribution_pipeline"
              element={
                <ProtectedRoute>
                  <DistributionPipeline />
                </ProtectedRoute>
              }
            />

            {/* Dev-only: combo analytics explorer */}
            {config.ENVIRONMENT === 'development' && (
              <Route
                path="/dev/combo-analytics"
                element={
                  <ProtectedRoute>
                    <ComboAnalytics />
                  </ProtectedRoute>
                }
              />
            )}

            {/* Default redirect for unmatched routes */}
            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </div>
              </PortfolioKPIProvider>
              </PortfolioLensProvider>
              </StrategyProvider>
            </Router>
            </FrameworkProvider>
          </SelectedStockProvider>
        </BeginnerProgressProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;
